// WALLET-P1 §6.1: the low-balance warning level, a modal editing `low_watermark_bp` as a percent
// of the last top-up (`wallet.updateSettings`).
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  Input,
} from "@companyos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PencilIcon } from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { problemText } from "../ops-parts";
import { RowAction } from "../settings/row-actions";
import { bpText, dollars, parsePercent } from "./billing-money";
import { billingKeys, type WalletView } from "./billing-queries";

const FLOOR = 5_000_000;

export function WarningLevelDialog({
  api,
  company,
  wallet,
}: {
  api: Api;
  company: CompanyAccess;
  wallet: WalletView;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(bpText(wallet.low_watermark_bp).replace("%", ""));
  const id = useId();
  const bp = parsePercent(value);
  const bad = bp === null || bp > 10_000;
  const preview =
    bp !== null && wallet.last_topup_micros !== null
      ? Math.max(FLOOR, Number((BigInt(wallet.last_topup_micros) * BigInt(bp)) / 10_000n))
      : null;
  const save = useMutation({
    mutationFn: (low_watermark_bp: number) =>
      api.client.wallet.updateSettings({ params: { co: company.id }, body: { low_watermark_bp } }),
    onSuccess: (next) => {
      qc.setQueryData(billingKeys.wallet(company.id), next);
      setOpen(false);
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!bad && bp !== null) save.mutate(bp);
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) setValue(bpText(wallet.low_watermark_bp).replace("%", ""));
      }}
    >
      <RowAction
        icon={PencilIcon}
        label="Edit low-balance warning"
        tip="Edit"
        onClick={() => setOpen(true)}
      />
      <DialogContent>
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>Low-balance warning</DialogTitle>
          </DialogHeader>
          <Field data-invalid={bad}>
            <FieldLabel htmlFor={id}>% of last top-up</FieldLabel>
            <Input
              id={id}
              inputMode="decimal"
              value={value}
              aria-invalid={bad}
              onChange={(e) => setValue(e.target.value)}
            />
            <FieldDescription>
              {preview !== null ? `Warns below ${dollars(preview)}. ` : ""}Never below $5.00.
            </FieldDescription>
            {bad ? <FieldError>Enter a percent from 0 to 100.</FieldError> : null}
            {save.isError ? (
              <FieldError>{problemText(save.error, "The warning level was not saved.")}</FieldError>
            ) : null}
          </Field>
          <DialogFooter>
            <DialogClose render={<Button variant="secondary" type="button" />}>Cancel</DialogClose>
            <Button type="submit" disabled={bad || save.isPending}>
              {save.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
